'use client';

import "@loschmidt/molstar/dist/index.css"

import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { MolstarWrapper, ViewerOptionsType } from "@loschmidt/molstar";
import { MolstarViewerProps } from "./types";
import { useProtein } from "./use-protein";
import { useLigands } from "./use-ligands";
import { useVisualizedResidues } from "./use-visualized-residues";
import { useFocusResidue } from "./use-focus-residue";

const ViewerOptions: ViewerOptionsType = {
  layoutIsExpanded: false,
  layoutShowControls: false,
  layoutShowRemoteState: false,
  layoutShowSequence: false,
  layoutShowLog: false,
  layoutShowLeftPanel: false,
  layoutShowStructure: false,

  viewportShowExpand: false,
  viewportShowControls: false,
  viewportShowSettings: false,
  viewportShowSelectionMode: false,
  viewportShowAnimation: true,
};

function MolstarViewer(props: MolstarViewerProps) {

  const {
    forwardedRef,
    wtProtein,
    mutProtein,
    wtResidues,
    mutResidues,
    ligands,
  } = props;

  const [divEl, setDivEl] = useState<HTMLDivElement | null>(null);
  const viewerInitialized = useRef(false);
  const [viewer, setViewer] = useState<MolstarWrapper>();

  // Initialize viewer
  useEffect(() => {
    if (divEl && !viewerInitialized.current) {
      viewerInitialized.current = true;
      (async () => {
        const viewer = await MolstarWrapper.init(divEl, ViewerOptions);
        viewer.setBackground(0xffffff);
        setViewer(viewer);
      })();
    }
  }, [divEl]);

  // Render proteins
  const wtProteinModel = useProtein(viewer, wtProtein);
  const mutProteinModel = useProtein(viewer, mutProtein);

  // Render visualized position groups
  useVisualizedResidues(viewer, wtProteinModel, wtResidues);
  useVisualizedResidues(viewer, mutProteinModel, mutResidues);

  const focusWtResidue = useFocusResidue(viewer, wtProteinModel);
  const focusMutResidue = useFocusResidue(viewer, mutProteinModel);

  // Render ligands
  const { focusLigand } = useLigands(viewer, ligands);

  useImperativeHandle(forwardedRef, () => ({
    focusLigand,
    focusWtResidue,
    focusMutResidue,
  }), [focusLigand, focusWtResidue, focusMutResidue]);

  return (
    <div className="ll-molstar-viewer" ref={setDivEl} />
  )
}

export default MolstarViewer;